<template>
    <div class="container">
        <h1>
            {{ $t('interventionRequest.singlePage.title') }} {{ this.$route.params.id }}
            <ReglePastille cle="suiviDemandeInterventionDetail.actionsNonPlanifiee" />
            <ReglePastille cle="suiviDemandeInterventionDetail.rapportDisponible" />
        </h1>
        <BorderedIconButton
            :imgUrl="imgUrl"
            :onClick="this.$router.back"
            :title="$t('bouton.retour')"
            className="back-btn"
        />
        <div class="actions-container">
            <BorderedIconButton
                v-if="isNonPlanifiee"
                :imgUrl="deleteImgUrl"
                :onClick="openDeleteModal"
                :title="$t('bouton.supprimer')"
                className="btn-supprimer"
            />
            <ButtonBlue
                v-if="isNonPlanifiee"
                :onClick="sendReminder"
                :text="$t('dataList.relancer')"
                class="btn-relance"
            />
        </div>
        <div class="body-container">
            <StatusTag v-if="status !== null" :status="this.status" />
            <DataList
                v-if="this.demandeInterventionData && this.isLoaded"
                :data="this.demandeInterventionData"
                :stop-propa-on-pdf="true"
                @downloadPdf="downloadPdf"
                @select="goToPage"
            />

            <AttachmentModal v-if="attachment" :content-type="contentType" :data="attachment" @close="closeModal" />
            <WaitingModal v-if="isAttachmentLoading" />
            <SuccessModal
                v-if="success"
                :subtitle="$t('intervention.success.reminder')"
                :title="''"
                @cancel="closeSuccess"
            />
            <SuccessModal
                v-if="showDeleteSuccessModal"
                :subtitle="$t('intervention.request.delete.success', { id: this.$route.params.id })"
                :title="''"
                @cancel="closeDeleteSuccess"
            />
            <Modal v-if="showDeleteModal" @cancel="closeDeleteModal">
                <ModalCardConfirm
                    :activeValidate="true"
                    :headerTitle="$t('intervention.deleteModal.title', { id: this.$route.params.id })"
                    @cancel="closeDeleteModal"
                    @confirm="deleteDemande"
                >
                    <span>{{ $t('intervention.request.deleteConfirm') }}</span>
                </ModalCardConfirm>
            </Modal>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import DemandeInterventionService from '../services/DemandeInterventionService';
import StatusTag, { statusName } from '@/components/ui-components/StatusTag.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { DemandeInterventionActions } from '../store/action-types';
import { store } from '@/plugins/store';
import AttachmentModal from '@/components/ui-components/AttachmentModal.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCardConfirm from '@/components/ui-components/ModalCardConfirm.vue';
import { DEMANDE_INTERVENTION } from '../store/mutation-types';
import router from '@/router';
import DownloadPdf from '@/common/utils/DownloadPdf';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    demandeInterventionData: Array<any>;
    status: string | null;
    isLoaded: boolean;
    isNonPlanifiee: boolean;
};

interface Intervention {
    id: number;
    dateDebutPrevue: 'string';
    dateDebutReelle: 'string';
    objet: 'string';
    etat: 'string';
    isRapportPresent: boolean;
}

export default defineComponent({
    name: 'InterventionRequestSinglePage',
    components: {
        ButtonBlue,
        DataList,
        StatusTag,
        BorderedIconButton,
        AttachmentModal,
        WaitingModal,
        SuccessModal,
        Modal,
        ModalCardConfirm,
        ReglePastille,
    },
    data(): DataType & { success: boolean; showDeleteModal: boolean; showDeleteSuccessModal: boolean } {
        return {
            demandeInterventionData: [],
            isLoaded: false,
            status: null,
            success: false,
            isNonPlanifiee: false,
            showDeleteModal: false,
            showDeleteSuccessModal: false,
        };
    },
    beforeMount() {
        DemandeInterventionService.getDemandeIntervention(+this.$route.params.id)
            .then((res: any) => {
                this.status = statusName(res.data.statut);
                this.isNonPlanifiee = res.data.statut === 'NON_PLANIFIEE';
                this.demandeInterventionData = [
                    { label: this.$t('dataList.demandeId'), value: res.data.id },
                    { label: this.$t('dataList.libelle'), value: res.data.libelle },
                    {
                        label: this.$t('dataList.dateEmission'),
                        value: new Date(res.data.dateEmission).toLocaleString('fr-FR',
                        {
                            dateStyle: 'short',
                            timeStyle: 'short'
                        }
                        ),
            },
                    { label: this.$t('dataList.demandeur'), value: res.data.demandeur },
                    { label: this.$t('dataList.type'), value: res.data.type },
                    { label: this.$t('dataList.delai'), value: res.data.delai },
                    {
                        label: this.$t('intervention.summary.date'),
                        value: res.data.dateAutre
                            ? new Date(res.data.dateAutre).toLocaleDateString().replaceAll('-', ' / ')
                            : '',
                    },
                    {
                        label: this.$t('dataList.equipment'),
                        value: res.data.phases?.[0]
                            ? res.data.phases[0].phaseLibelle && res.data.phases[0].phaseLibelle.trim().toLowerCase() !== 'maintenance'
                                ? `${res.data.phases[0].libelleEquipement} - ${res.data.phases[0].phaseLibelle}`
                                : res.data.phases[0].libelleEquipement
                            : '',
                    },
                    {
                        label: this.$t('dataList.sujetRecurrent'),
                        value: res.data.sujetRecurrent ? this.$t('yes') : this.$t('no'),
                    },
                    {
                        label: this.$t('dataList.contact'),
                        value: `${res.data?.contactSurSite ? res.data?.contactSurSite : ''} ${
                            res.data.contactSurSiteTelephoneMobile
                                ? ` - ${this.$t('intervention.summary.phone')} ${
                                      res.data.contactSurSiteTelephoneMobile
                                  }`
                                : ''
                        } ${
                            res.data.contactSurSiteTelephoneFixe
                                ? ` - ${this.$t('intervention.summary.phone')} ${res.data.contactSurSiteTelephoneFixe}`
                                : ''
                        }`,
                    },
                    {
                        label: this.$t('dataList.dateRelance'),
                        value:
                            res.data.historiqueRelancesDI && res.data.historiqueRelancesDI.length > 0
                                ? res.data.historiqueRelancesDI
                                      .map((h: any) => new Date(h.dateRelance).toLocaleDateString())
                                      .join(' - ')
                                : '',
                    },
                    { label: this.$t('dataList.follow'), value: '' },
                    { label: this.$t('dataList.description'), value: res.data.description },
                ];
                const contactsSuivi = () => {
                    const tab = [];
                    if (res.data.contactsSuivi?.length > 0) {
                        tab.push({ label: this.$t('dataList.followedBy'), value: res.data.contactsSuivi[0] });
                        for (let index = 1; index < res.data.contactsSuivi.length; index++) {
                            tab.push({ value: res.data.contactsSuivi[index] });
                        }
                    }
                    return tab;
                };
                this.demandeInterventionData = this.demandeInterventionData.concat(contactsSuivi());

                if (res.data.piecesJointes) {
                    const pj = true;
                    const pjs = res.data.piecesJointes;
                    const action = `suiviDemandeInterventionModuleState/${DemandeInterventionActions.GET_ATTACHMENT}`;

                    this.demandeInterventionData.push({
                        label: this.$t('dataList.attachments', res.data.piecesJointes?.length),
                        value: { pj, pjs, action },
                    });
                }

                this.isLoaded = true;
                DemandeInterventionService.getInterventionsByDemandeInterventionId(+this.$route.params.id).then(res => {
                    const tableHeader = [
                        { label: this.$t('label.numIntervention') },
                        { label: this.$t('label.predictedDate') },
                        { label: this.$t('label.realDate') },
                        { label: this.$t('label.object') },
                        { label: this.$t('label.state') },
                        { label: this.$t('label.rapport') },
                    ];

                    const tableData = res.data.map(
                        ({ id, dateDebutPrevue, dateDebutReelle, objet, etat, isRapportPresent }: Intervention) => ({
                            id: { pageName: 'interventionDetailView', value: id, label: id },
                            dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                            dateDebutReelle: dateDebutReelle ? new Date(dateDebutReelle).toLocaleDateString() : '',
                            objet,
                            statut: statusName(etat),
                            pdfExist: isRapportPresent,
                        })
                    );

                    const noDataText = this.$t('interventionRequest.singlePage.noIntervention');

                    this.demandeInterventionData.push({
                        label: this.$t('dataList.interventionListlinked'),
                        value: { tableHeader, tableData, noDataText },
                    });
                });
            })
            .catch(async (err: any) => {
                const message = await ErrorManager.processError(err.response);

                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            });
    },
    computed: {
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
        deleteImgUrl() {
            return require('@/assets/img/delete.svg');
        },
        attachment() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachment'];
        },
        isAttachmentLoading() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsIsAttachmentLoading'];
        },
        contentType() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachmentType'];
        },
    },
    methods: {
        closeModal() {
            store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_ATTACHMENT}`, null);
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        async downloadPdf(item: any) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
        openDeleteModal() {
            this.showDeleteModal = true;
        },
        closeDeleteModal() {
            this.showDeleteModal = false;
        },
        async deleteDemande() {
            try {
                const id = +this.$route.params.id;
                await DemandeInterventionService.supprimerDemandeIntervention(id);
                this.$store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.REMOVE_ITEM}`, id);
                this.closeDeleteModal();
                this.showDeleteSuccessModal = true;
            } catch (err: any) {
                const message = await ErrorManager.processError(err.response);
                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            }
        },
        async sendReminder() {
            try {
                const ok = await store.dispatch(
                    `suiviDemandeInterventionModuleState/${DemandeInterventionActions.SEND_ASK_INTERVENTION_REMINDER}`,
                    +this.$route.params.id
                );
                if (ok) {
                    this.success = true;
                }
            } catch (e) {
                // errors are handled by store (toaster), nothing to do here
            }
        },
        closeSuccess() {
            this.success = false;
        },
        closeDeleteSuccess() {
            this.showDeleteSuccessModal = false;
            this.$router.back();
        },
    },
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
.body-container {
    display: flex;
    flex-direction: column;
    flex: 1;
}
.actions-container {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 16px;
    position: absolute;
    right: 24px;
    top: 50px;
}
.btn-relance {
    padding: 8px;
    border-radius: 6px;
    height: 33px;
    border: 1px solid var(--color-principal);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
}
.btn-supprimer {
    padding: 8px;
    border-radius: 6px;
    height: 33px;
    border: 1px solid var(--color-principal);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
}
h1 {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin: 0 0 15px 0;
}
</style>
